import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AttentionTriangleIcon,
  EyeShowIcon,
  EyeHideIcon,
  FullscreenIcon,
} from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import Loader from '@rsos/components/capstone/Loader';
import { Absolute } from '@rsos/components/capstone/base';
import {
  useGetMediaSourceQuery,
  endpoint,
} from '@rsos/reduxDataStore/src/features/media/mediaAPI';
import { trackPlayVideo, trackMediaSourceRequest } from '../../mediaTrackings';
import {
  ErrorContainer,
  IconWrapper,
  MainContainer,
  MainImg,
  MainVideo,
  MediaContainer,
  PreviewContainer,
  PreviewImg,
  PreviewVideo,
} from './Carousel.styles';

const PREFIX_IMAGE = 'image/';
const PREFIX_VIDEO = 'video/';

/**
 * Current Carousel component
 * @param {Object} data - Image or video data
 * @param {Boolean} isBlurred - are the Carousel media blurred
 * @param {Boolean} isFullscreen - is the Carousel fullscreen
 * @param {Function} onBlur - Function to handle blurring/unblurring media
 * @param {Function} onFullscreen - Function to handle showing the full carousel
 * in fullscreen
 */
export const CurrentCarouselItem = React.memo(
  ({ data, isBlurred, isFullscreen, onBlur, onFullscreen }) => {
    const videoRef = useRef();
    const imgRef = useRef();

    const queryKey = useMemo(
      () => data?.media_url.split('/').slice(-1)[0],
      [data],
    );
    const {
      data: fetchedData,
      isFetching,
      error,
    } = useGetMediaSourceQuery(queryKey);
    const mediaData = useMemo(() => fetchedData?.urls?.[0]?.url, [fetchedData]);
    const contentType = useMemo(
      () => fetchedData?.urls?.[0]?.content_type,
      [fetchedData],
    );

    const handleBlurEffect = useCallback(() => {
      onBlur();
    }, [onBlur]);

    const handleFullscreen = useCallback(() => {
      onFullscreen();
    }, [onFullscreen]);

    const handleMediaUploadVideoClick = e => {
      if (e.target.getAttribute('data-name') == 'media-play-button') {
        trackPlayVideo(mediaData);
      }
    };

    useEffect(() => {
      if (
        contentType?.startsWith(PREFIX_VIDEO) &&
        videoRef.current &&
        isBlurred
      ) {
        videoRef.current.pause();
      }
    }, [contentType, isBlurred, videoRef]);

    // Track media requests
    useEffect(() => {
      if (mediaData != undefined || error != undefined) {
        trackMediaSourceRequest({
          mediaUrl: mediaData,
          contentType,
          requestUrl: `${endpoint}/retrieve/${queryKey}`,
          responseSuccess: !error,
          isPreview: false,
        });
      }
    }, [mediaData, contentType, queryKey, error]);

    // Force the video to load the new url when switching between videos
    useEffect(() => {
      if (contentType?.startsWith(PREFIX_VIDEO) && videoRef.current) {
        videoRef?.current?.load();
      }
    }, [contentType, mediaData]);

    if (isFetching) {
      return (
        <MainContainer isFullscreen={isFullscreen}>
          <Loader />
        </MainContainer>
      );
    } else if (error) {
      return (
        <ErrorContainer isFullscreen={isFullscreen}>
          <AttentionTriangleIcon width={24} height={24} color="secondaryText" />
          <Text color="secondaryText" mt="8px">
            Media Failed to load
          </Text>
          <Absolute center bottom="0" right="0">
            <IconWrapper onClick={handleFullscreen}>
              <FullscreenIcon width="16px" height="16px" />
            </IconWrapper>
          </Absolute>
        </ErrorContainer>
      );
    } else {
      if (contentType.startsWith(PREFIX_IMAGE)) {
        return (
          <MainContainer className="MainContainer" isFullscreen={isFullscreen}>
            <MediaContainer>
              <MainImg
                src={mediaData}
                isBlurred={isBlurred}
                isFullscreen={isFullscreen}
                ref={imgRef}
              />
            </MediaContainer>
            <Absolute center bottom="0" right="0">
              <IconWrapper onClick={handleBlurEffect}>
                {isBlurred ? (
                  <EyeHideIcon
                    width="16px"
                    height="16px"
                    viewboxHeight={16}
                    viewboxWidth={16}
                    padding="30px"
                  />
                ) : (
                  <EyeShowIcon
                    width="16px"
                    height="16px"
                    viewboxHeight={16}
                    viewboxWidth={16}
                    padding="30px"
                  />
                )}
              </IconWrapper>
              <IconWrapper onClick={handleFullscreen}>
                <FullscreenIcon width="16px" height="16px" />
              </IconWrapper>
            </Absolute>
          </MainContainer>
        );
      } else if (contentType.startsWith(PREFIX_VIDEO)) {
        return (
          <MainContainer className="MainContainer" isFullscreen={isFullscreen}>
            <MediaContainer>
              <MainVideo
                controls={!isBlurred}
                controlsList="nofullscreen"
                isBlurred={isBlurred}
                isFullscreen={isFullscreen}
                onContextMenu={e => {
                  e.preventDefault(); // Prevent right clicks}
                }}
                ref={videoRef}
                onClick={e => handleMediaUploadVideoClick(e)}
                data-name="media-play-button"
              >
                <source src={mediaData} type="video/mp4" />
                <source src={mediaData} type="video/ogg" />
                Your browser does not support the video tag.
              </MainVideo>
            </MediaContainer>
            <Absolute center bottom="0" right="0">
              <IconWrapper onClick={handleBlurEffect}>
                {isBlurred ? (
                  <EyeHideIcon
                    width="16px"
                    height="16px"
                    viewboxHeight={16}
                    viewboxWidth={16}
                    padding="30px"
                  />
                ) : (
                  <EyeShowIcon
                    width="16px"
                    height="16px"
                    viewboxHeight={16}
                    viewboxWidth={16}
                    padding="30px"
                  />
                )}
              </IconWrapper>
              <IconWrapper onClick={handleFullscreen}>
                <FullscreenIcon width="16px" height="16px" />
              </IconWrapper>
            </Absolute>
          </MainContainer>
        );
      }
      // Fallback
      return (
        <ErrorContainer isFullscreen={isFullscreen}>
          <AttentionTriangleIcon width={24} height={24} color="secondaryText" />
          <Text color="secondaryText" mt="8px">
            Media Failed to load
          </Text>
          <Absolute center bottom="0" right="0">
            <IconWrapper onClick={handleBlurEffect}>
              {isBlurred ? (
                <EyeHideIcon
                  width="16px"
                  height="16px"
                  viewboxHeight={16}
                  viewboxWidth={16}
                  padding="30px"
                />
              ) : (
                <EyeShowIcon
                  width="16px"
                  height="16px"
                  viewboxHeight={16}
                  viewboxWidth={16}
                  padding="30px"
                />
              )}
            </IconWrapper>
            <IconWrapper onClick={handleFullscreen}>
              <FullscreenIcon width="16px" height="16px" />
            </IconWrapper>
          </Absolute>
        </ErrorContainer>
      );
    }
  },
);

/**
 * Preview component
 * @param {Object} data - Image or video data
 * @param {Boolean} highlight - Flag for applying border style
 * @param {Boolean} isBlurred - are the Carousel media blurred
 * @param {Function} onSetCurrentData - Callback function to view image or video
 * in CurrentCarouselItem
 */
export const CarouselPreviewItem = React.memo(
  ({ data, highlight, isBlurred, isFullscreen, onSetCurrentData }) => {
    const queryKey = data?.media_thumb_url.split('/').slice(-1)[0];
    const {
      data: fetchedData,
      isFetching,
      error,
    } = useGetMediaSourceQuery(queryKey);

    const mediaData = useMemo(() => fetchedData?.urls?.[0]?.url, [fetchedData]);
    const contentType = useMemo(
      () => fetchedData?.urls?.[0]?.content_type,
      [fetchedData],
    );

    const handleOnClick = useCallback(() => {
      onSetCurrentData(data);
    }, [data, onSetCurrentData]);

    // Track media requests
    useEffect(() => {
      if (mediaData != undefined || error != undefined) {
        trackMediaSourceRequest({
          mediaUrl: mediaData,
          contentType,
          requestUrl: `${endpoint}/retrieve/${queryKey}`,
          responseSuccess: !error,
          isPreview: true,
        });
      }
    }, [mediaData, contentType, queryKey, error]);

    return (
      <PreviewContainer highlight={highlight} onClick={handleOnClick}>
        {isFetching ? (
          <Loader />
        ) : error ? (
          <Box
            align="center"
            justify="center"
            height="80px"
            width="100%"
            bg="foreground"
          >
            <AttentionTriangleIcon
              width={16}
              height={16}
              color="secondaryText"
            />
          </Box>
        ) : (
          <>
            {contentType.startsWith(PREFIX_VIDEO) ? (
              <>
                <PreviewVideo
                  controls={false}
                  isBlurred={isBlurred}
                  isFullscreen={isFullscreen}
                  onContextMenu={e => {
                    e.preventDefault(); // Prevent right clicks}
                  }}
                  src={mediaData}
                />
              </>
            ) : (
              <PreviewImg
                isBlurred={isBlurred}
                isFullscreen={isFullscreen}
                src={mediaData}
              />
            )}
          </>
        )}
      </PreviewContainer>
    );
  },
);
